import React from 'react';
import { motion } from 'framer-motion';
import ObjectiveSelector from '@/components/objectives/ObjectiveSelector';
import ObjectiveDescription from '@/components/objectives/ObjectiveDescription';
import { Objective } from '@/types/objective';

interface ObjectiveSectionProps {
  objectives: Objective[];
  selectedObjective: string;
  onSelect: (objective: string) => void;
  isOpen: boolean;
  onToggle: () => void;
  needsAttention?: boolean;
  selectedObjectiveData: Objective | null;
  dropdownRef: React.RefObject<HTMLDivElement>;
}

export function ObjectiveSection({
  objectives,
  selectedObjective,
  onSelect,
  isOpen,
  onToggle,
  needsAttention,
  selectedObjectiveData,
  dropdownRef
}: ObjectiveSectionProps) {
  const objectiveImageUrl = 'https://firebasestorage.googleapis.com/v0/b/insightalks-47e16.firebasestorage.app/o/objectivephoto.webp?alt=media&token=3a0a0d4c-76fa-4d64-bdef-bcb0376c70c5';

  return (
    <div className="mb-12">
      {/* Mobile Illustration */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="lg:hidden mb-8"
      >
        <img
          src={objectiveImageUrl}
          alt="Choose your objective illustration"
          className="w-full h-auto max-h-[300px] object-contain mx-auto"
        />
      </motion.div>

      <div className="max-w-6xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
          {/* Desktop Illustration */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
            className="hidden lg:block h-[440px]"
          >
            <img
              src={objectiveImageUrl}
              alt="Choose your objective illustration"
              className="w-full h-[440px] max-w-md mx-auto object-contain"
            />
          </motion.div>

          {/* Content Section */}
          <div className="flex flex-col">
            <div className="text-center lg:text-left">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="mb-12"
              >
                <div className="relative">
                  <h2 className="text-4xl md:text-5xl font-bold text-gray-900 leading-tight pb-2 mb-6">
                    Choose the plan that best fits your growth journey
                  </h2>
                  <div className="w-24 h-1 bg-gradient-to-r from-primary to-secondary rounded-full mx-auto lg:mx-0 mb-6"></div>
                  <p className="text-lg md:text-xl text-gray-600 leading-relaxed max-w-2xl">
                    All options are built upon a validated core survey, complemented by tailored, case-specific questions to better align with your objectives.
                  </p>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="w-full"
                id="objective-selector"
                ref={dropdownRef}
              >
                <ObjectiveSelector
                  objectives={objectives}
                  selectedObjective={selectedObjective}
                  onSelect={onSelect}
                  isOpen={isOpen}
                  onToggle={onToggle}
                  needsAttention={needsAttention}
                />
                <ObjectiveDescription objective={selectedObjectiveData} />
              </motion.div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}